import { Suspense } from 'react';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { AuthenticationProvider } from '@zenbusiness/application-commons-authentication';
import {
  BusinessEntityProvider,
  CheckBusinessEntity
} from '@zenbusiness/application-commons-business-entity';
import { SlideCheckoutProvider } from '@zenbusiness/application-commons-ecommerce';
import useEnv from '@zenbusiness/application-commons-env';
import {
  ExperimentsProvider,
  ExperimentsIdentity
} from '@zenbusiness/application-commons-experiments';
import { GraphQLProvider } from '@zenbusiness/application-commons-graphql';
import { IdentityProvider } from '@zenbusiness/application-commons-identity';
import { LoggingProvider, ErrorBoundary } from '@zenbusiness/application-commons-logging';
import { VisitProvider } from '@zenbusiness/application-commons-visit';
import { WebView } from '@zenbusiness/application-commons-webview';
import PageLoading from '@zenbusiness/zui/PageLoading';
import { ZuiThemeProvider } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';

import { ToastProvider } from '#client/hooks/useToastContext';
import { PLAID, NAME, DEFAULT_SEGMENT, RELEASE_VERSION } from '#client/manifest';
import EmptyPage from '#client/pages/Empty';
import ErrorPage from '#client/pages/Error';
import { isProduction } from '#client/utils/environment';
import { gqlErrorHandler } from '#client/utils/errors';
import { initPlaid } from '#client/utils/Plaid/PlaidService';

const emotionCache = createCache({
  key: 'zen'
});

function Providers({ children, federated = false }) {
  const {
    GRAPHQL_URL,
    AUTH0_CLIENTID,
    AUTH0_DOMAIN,
    AUTH0_AUDIENCE,
    LAUNCHDARKLEY_CLIENT_ID,
    DEPLOY_ENV,
    IS_LOCAL,
    LOCAL_LOGIN_APP_ORIGIN,
    OIDC_CLIENT_ID,
    OIDC_LOGIN_ENABLED
  } = useEnv('head-first');

  const isProd = isProduction(DEPLOY_ENV);
  initPlaid(PLAID, isProd);

  const { isWebView } = WebView;

  return (
    <LoggingProvider
      normalizeErrors
      tag-frontend="banking-ui"
      tag-frontend-release={`${NAME}-${RELEASE_VERSION || 'unset'}`}
      tag-isWebView={String(isWebView)}
    >
      <ErrorBoundary
        fallback={<ErrorPage />}
        id={`${NAME}/App/Providers`}
      >
        <CacheProvider value={emotionCache}>
          <ZuiThemeProvider
            baseline={!federated}
            scopedBaseline={federated}
          >
            <ExperimentsProvider
              appId={NAME}
              clientId={LAUNCHDARKLEY_CLIENT_ID}
              deployEnv={DEPLOY_ENV}
            >
              <AuthenticationProvider
                audience={AUTH0_AUDIENCE}
                clientID={AUTH0_CLIENTID}
                domain={AUTH0_DOMAIN}
                enableOidcLogin={OIDC_LOGIN_ENABLED}
                isLocal={IS_LOCAL}
                localLoginAppOrigin={LOCAL_LOGIN_APP_ORIGIN}
                oidcClientId={OIDC_CLIENT_ID}
                userType="customer"
                useZbSessionInfo
              >
                <GraphQLProvider
                  memoizeData
                  onError={gqlErrorHandler}
                  url={GRAPHQL_URL}
                >
                  <VisitProvider disableCreation>
                    <IdentityProvider>
                      <BusinessEntityProvider
                        path={`${DEFAULT_SEGMENT}/company/:businessEntityUuid/*`}
                      >
                        <CheckBusinessEntity
                          loadingElement={<PageLoading />}
                          noRedirect
                        >
                          <SlideCheckoutProvider>
                            {!federated && <ExperimentsIdentity />}
                            <ToastProvider>
                              <Suspense fallback={<EmptyPage />}>{children}</Suspense>
                            </ToastProvider>
                          </SlideCheckoutProvider>
                        </CheckBusinessEntity>
                      </BusinessEntityProvider>
                    </IdentityProvider>
                  </VisitProvider>
                </GraphQLProvider>
              </AuthenticationProvider>
            </ExperimentsProvider>
          </ZuiThemeProvider>
        </CacheProvider>
      </ErrorBoundary>
    </LoggingProvider>
  );
}

Providers.propTypes = {
  federated: PropTypes.bool
};

export default Providers;
